import React = require('react');
import {
  IChannel,
  IDestination,
  IOptionMap,
  IPremium,
  IProduct,
  IQuotes,
  IStateCities,
  ITraveller,
  TripType
} from '../../../../../../data/api/models';
import { filter, find, get, map, startCase } from 'lodash';
import QuoteIcons from '../../../Components/Icon/QuoteIcons';
import { FormattedNumber } from 'react-intl';
import { getTripDuration, replaceBenefitTextPlaceHolder } from '../../../../../../helpers/Quote';
import GroupTypeSelection from '../../../Components/GroupTypeSelection';
import { useEffect, useState } from 'react';
import { II18nMessages } from '../../../../../../services/branding/models';
import { DestinationRegionSelector } from '../../../Components/DestinationRegionSelector';
import { RecalculateButton } from '../../../Components/RecalculateButton';
import { AdmiralCard } from '../../../Components/AdmiralCard';
import { StartDateSelector } from './components/StartDateSelector';
import { EndDateSelector } from './components/EndDateSelector';
import {
  Collapsable,
  ICollapsableComponent,
  IQuoteActions,
  IQuoteState
} from '../../../../../../services/quote/models';
import { AdditionalCover } from '../../../Components/AdditionalCover';
import { Button, Col, ModalBody, ModalFooter, ModalHeader, ModalTitle, Row } from 'react-bootstrap';
import { EditSectionButton } from './components/EditSectionButton';
import { SchemeChoice } from '../../../Components/SchemeChoice';
import { AggTravellersEdit } from './components/AggTravellersEdit';
import { InjectedFormProps } from 'redux-form';
import { FaSpinner } from 'react-icons/fa';
import { IVeriskState } from '../../../../../../services/verisk/models';
import DefinitionPopup from '../../../../../../components/DefinitionPopup';
import { windowSize } from '../../../../../../hooks/windowSize';
import { Modal as BootstrapModal } from 'react-bootstrap/lib';
import { setCollapsable } from '../../../../../../services/quote/reducer';

interface IQuoteSummaryProps extends InjectedFormProps {
  travellers: ITraveller[];
  tripType: TripType;
  region: string;
  quotes: IQuotes;
  destinationsValue: string[];
  channel: IChannel;
  premium: IPremium;
  options: IOptionMap;
  product: IProduct;
  schemeId: string;
  startDate: string;
  endDate: string;
  groupType: string;
  i18n: II18nMessages;
  quoteState: IQuoteState;
  synchronousError: any;
  stateCitiesValue: string[];
  destinations: IDestination[];
  stateCities: IStateCities[];
  verisk: IVeriskState;
  onBack?: () => void;
  travellersInEditmode?: (value: boolean) => void;
  medicalHistory?: boolean;
  maxTripDuration: any;
  collapsable: ICollapsableComponent
  actions: {
    dispatch: any;
    quote: IQuoteActions;
    submit(name: string);
  }

  change(field: string, value: any): void;

  renderDefinition(name: string, useProvidedString?: boolean): any;

  recalculate: (calculateAll?: boolean) => any;
  valid: boolean;
}

interface IDisplayBenefits {
  text: string;
  name: string;
}

export const QuoteSummary = ({
                               travellers,
                               tripType,
                               region,
                               destinations,
                               destinationsValue,
                               premium,
                               channel,
                               options,
                               i18n,
                               quoteState,
                               quotes,
                               product,
                               schemeId,
                               startDate,
                               endDate,
                               renderDefinition,
                               groupType,
                               synchronousError,
                               recalculate,
                               stateCities,
                               stateCitiesValue,
                               verisk,
                               valid,
                               actions,
                               onBack,
                               medicalHistory,
                               travellersInEditmode = null,
                               maxTripDuration,
                               collapsable,
                               ...props
                             }: IQuoteSummaryProps) => {

  const isAnnual = tripType === 'annual';
  const [showEditTravellers, setShowEditTravellers] = useState(false);
  const [showEditDestination, setShowEditDestination] = useState(false);
  const [showEditDates, setShowEditDates] = useState(false);
  const [showEditOptions, setShowEditOptions] = useState(false);
  const [showEditScheme, setShowEditScheme] = useState(false);
  const [showEditSAggregatorTravellers, setShowEditSAggregatorTravellers] = useState(true);
  const [showMedicalScreeningModal, setShowMedicalScreeningModal] = useState(false);

  const isCalculating = quoteState.isCalculating;
  const isAggregatorChannel = get(channel, 'channelType', '') === 'AGG';
  const coverLevel = get(premium, 'scheme.coverLevel');
  const isMobile = windowSize(480).isMobile;

  useEffect(() => {
    if (travellersInEditmode) {
      travellersInEditmode(true)
    }
  }, [])

  const editTravellers = () => {
    setShowEditTravellers(true)
  }

  const editAggTravellers = () => {
    setShowEditSAggregatorTravellers(true)
    if (travellersInEditmode) {
      travellersInEditmode(true)
    }
  }

  const editDestination = () => {
    setShowEditDestination(true)
  }

  const editTripDates = () => {
    setShowEditDates(true)
  }

  const editOptions = () => {
    setShowEditOptions(true)
    props.dispatch(setCollapsable(null, Collapsable.ADDITIONAL));
  }

  const closeEditOptions = () => {
    setShowEditOptions(false)
    props.dispatch(setCollapsable(Collapsable.ADDITIONAL));
  }

  const editScheme = () => {
    setShowEditScheme(true)
    recalculate(true);
  }

  const closeMedicalModal = () => {
    setShowMedicalScreeningModal(false)
  }

  const handleRecalculate = (calculateAll: boolean = false) => () => {
    closeAllEditMode();
    recalculate(calculateAll);
  }

  const closeAllEditMode = () => {
    setShowEditTravellers(false)
    setShowEditDestination(false)
    setShowEditDates(false)
    setShowEditOptions(false)
    setShowEditScheme(false)
    props.dispatch(setCollapsable(Collapsable.ADDITIONAL));
  }

  const handleNavigateToMedical = () => {
    if (medicalHistory) {
      props.change('medicalHistory', false)
    }
    onBack();
  }

  const handleRecalculateTravellers = (calculateAll: boolean = false) => {
    closeAllEditMode();
    recalculate(calculateAll);
    setShowMedicalScreeningModal(true);
  }

  const handleAggregatorSave = () => () => {
    setShowEditSAggregatorTravellers(false)
    if (travellersInEditmode) {
      travellersInEditmode(false)
    }
    recalculate();
  }

  const renderTravellersCard = () => {

    if (showEditTravellers) {
      return (
        <div className={'quote-summary-card-container'}>
          <GroupTypeSelection
            renderDefinition={renderDefinition}
            quoteState={quoteState}
            tripType={tripType}
            groupType={groupType}
            allowLeadEdit={true}
            i18n={i18n}
            product={product}
            valid={valid}
            recalculate={handleRecalculateTravellers}
            forceShowRecalculate={true}
            synchronousError={synchronousError}
            {...props}/>
        </div>
      )
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>Travellers</div>
          {isMobile && (<div className={'flex--stretch text-right'}><EditSectionButton isCalculating={isCalculating}
                                                                                       handleEdit={editTravellers}
                                                                                       hidden={isAggregatorChannel}/>
          </div>)}
          <div className={'flex--stretch'}>

            {map(travellers, (traveller, index) => {
              const addressLine1 = traveller?.address?.line1;
              return (
                <>
                  <div className={'traveller-info-container'} key={index}>
                    <div><QuoteIcons name={'Person'} color={'blue'}/></div>
                    <div className={'traveller-info'}>
                      <div
                        className={'title'}>{traveller.title + ' ' + traveller.firstName + ' ' + traveller.lastName}</div>
                      <div>{traveller.dob}</div>
                      <div>{addressLine1 ? addressLine1 + ', ' + traveller?.address?.postcode : ''}</div>
                      <div>{traveller?.email}</div>
                      <div>{traveller?.phone}</div>
                    </div>
                  </div>
                </>
              )
            })}
          </div>
          {!isMobile && (<div className={'flex--stretch text-right'}><EditSectionButton isCalculating={isCalculating}
                                                                                        handleEdit={editTravellers}
                                                                                        hidden={isAggregatorChannel}/>
          </div>)}
        </div>
      </div>
    )
  }

  const renderAggregatorTravellersCard = () => {

    if (showEditSAggregatorTravellers) {
      return (
        <div>
          <AdmiralCard noPadding={true} icon={'people.svg'} title={'Travellers'}
                       subTitle={'To complete your quote, we need some information about the travellers'}>
            <AggTravellersEdit travellers={travellers} channel={channel}
                               synchronousError={synchronousError} {...props}/>
          </AdmiralCard>
          <div className={'summary-button-container'}>
            <div className={'box--half'}/>
            <div className={'box--half pl-15'}>
              <Button
                className={'admiral-primary-btn'}
                block={true}
                type={'button'}
                onClick={handleAggregatorSave()}
                disabled={!valid || quoteState.isCalculating}
              >
                {quoteState.isCalculating && (<><FaSpinner className="fa-spin"/><span> SAVING</span></>)}
                {!quoteState.isCalculating && ('SAVE')}
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>Travellers</div>
          {isMobile && (<div className={'flex--stretch text-right'}><EditSectionButton isCalculating={isCalculating}
                                                                                       handleEdit={editAggTravellers}/>
          </div>)}
          <div className={'flex--stretch'}>

            {map(travellers, (traveller, index) => {
              return (
                <>
                  <div className={'traveller-info-container'} key={index}>
                    <div><QuoteIcons name={'Person'} color={'blue'}/></div>
                    <div className={'traveller-info'}>
                      <div
                        className={'title'}>{traveller.title + ' ' + traveller.firstName + ' ' + traveller.lastName}</div>
                      <div>{traveller.dob}</div>
                    </div>
                  </div>
                </>
              )
            })}
          </div>
          {!isMobile && (<div className={'flex--stretch text-right'}><EditSectionButton isCalculating={isCalculating}
                                                                                        handleEdit={editAggTravellers}/>
          </div>)}
        </div>
      </div>
    )

  }

  const renderDestinationRegion = () => {
    const title = isAnnual ? 'Region' : 'Destinations'

    if (showEditDestination) {
      return (
        <DestinationRegionSelector
          quoteState={quoteState}
          tripType={tripType}
          hideDateSelector={true}
          region={region}
          i18n={i18n}
          renderDefinition={renderDefinition}
          product={product}
          destinations={destinations}
          stateCities={stateCities}
          destinationsValue={destinationsValue}
          stateCitiesValue={stateCitiesValue}
          valid={valid}
          verisk={verisk}
          actions={actions}
          recalculate={handleRecalculate()}
          premium={premium}
          {...props}
        />
      )
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{title}</div>
          {isMobile && (
            <div className={'flex--stretch text-right'}>
              <EditSectionButton isCalculating={isCalculating}
                                 handleEdit={editDestination}
                                 hidden={isAggregatorChannel}/>
            </div>
          )}
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Globe'} color={'blue'}/></div>
              <div className={'traveller-info single'}>
                <div
                  className={'title'}>{isAnnual ? startCase(region) : (destinationsValue ? destinationsValue.toString() : '')}</div>
              </div>
            </div>
          </div>
          {!isMobile && (
            <div className={'flex--stretch text-right'}>
              <EditSectionButton isCalculating={isCalculating}
                                 handleEdit={editDestination}
                                 hidden={isAggregatorChannel}/>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderLevelOfCover = () => {
    const benefitsToDisplay: IDisplayBenefits[] = [
      { text: 'Medical Emergency Cover up to ', name: 'Emergency Medical Costs and Repatriation' },
      { text: 'Cancellation or cutting short your trip up to ', name: 'Cancellation or cutting your trip short' },
      { text: 'Personal Belongings Cover up to ', name: 'Personal belongings' },
      { text: 'Loss of money and documents up to ', name: 'Money, Passports and Travel Documents' }
    ]

    const travelDuration = isAnnual ? (
      <div className={'cover-level-list'}>
        <QuoteIcons name={'Tick'} color={'blue'}/>
        <div>Unlimited travel for up to 31 days per trip</div>
      </div>
    ) : '';

    if (showEditScheme) {
      return (
        <>
          <SchemeChoice
            tripType={tripType}
            quotes={quotes}
            schemeId={schemeId}
            options={options}
            product={product}
            startDate={startDate}
            region={region}
            i18n={i18n}
            maxTripDuration={maxTripDuration}
            synchronousError={synchronousError}
            quoteState={quoteState}
            actions={actions}
            channel={channel}
            valid={valid}
            renderDefinition={renderDefinition}

            recalculate={handleRecalculate(true)}
            {...props}
          />
          <div className={'close-selection'}>
            <Row>
              <Col xs={6}>
                <Button
                  block={true}
                  className={'admiral-white-btn'}
                  onClick={setShowEditScheme.bind(this, false)}
                >CANCEL</Button>
              </Col>
            </Row>
          </div>
        </>
      )
    }

    let iconColor = 'darkBlue';

    switch (coverLevel) {
      case 'gold':
        iconColor = 'gold';
        break;
      case 'platinum':
        iconColor = 'platinum'
        break;
      default:
        break;
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Level of cover'}</div>
          {isMobile && (<div className={'flex--stretch text-right'}><EditSectionButton isCalculating={isCalculating}
                                                                                       handleEdit={editScheme}
                                                                                       hidden={isAggregatorChannel}/>
          </div>)}
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'ShieldTicked'} color={iconColor}/></div>
              <div className={'traveller-info single'}>
                <div className={'title'}>{startCase(`${coverLevel} Cover`)}</div>
              </div>
            </div>
            {travelDuration}

            {map(benefitsToDisplay, (benefit: IDisplayBenefits, key) => {
              const selectedBenefit = find(get(premium, 'benefits'), ['name', benefit.name]);
              const displayValue = get(selectedBenefit, `coverLevelDisplay.${coverLevel}`);
              return (
                <div className={'cover-level-list'} key={key}>
                  <QuoteIcons name={'Tick'} color={'blue'}/>
                  <div>{benefit.text + displayValue}</div>
                </div>
              )
            })}

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div>{'Legal Liability'}</div>
            </div>

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div>{'Legal Protection'}</div>
            </div>
          </div>
          {!isMobile && (
            <EditSectionButton isCalculating={isCalculating} handleEdit={editScheme} hidden={isAggregatorChannel}/>)}
        </div>
      </div>
    )
  }

  const renderExtraCover = () => {
    const addedOptionNumber = filter(options, (o, key) => {
      return o === true;
    }).length

    const additionalCover = {
      cruise_cover: get(product, 'metadata.options.cruise_cover'),
      winter_sports: get(product, 'metadata.options.winter_sports'),
      gadget_cover: get(product, 'metadata.options.gadget_cover'),
      excess_waiver: get(product, 'metadata.options.excess_waiver'),
      golf_cover: get(product, 'metadata.options.golf_cover'),
    };

    if (showEditOptions) {
      return (
        <>
          <AdditionalCover
            {...props}
            quoteState={quoteState}
            recalculate={handleRecalculate()}
            product={product}
            channel={channel}
            options={options}
            startDate={startDate}
            endDate={endDate}
            tripType={tripType}
            valid={valid}
            selectedPremium={premium}
            collapsable={collapsable}
            hideContinueButton={true}
          />
          <div className={'close-selection'}>
            <Row>
              <Col xs={6}>
                <Button
                  block={true}
                  className={'admiral-white-btn'}
                  onClick={closeEditOptions}
                >CANCEL</Button>
              </Col>
            </Row>
          </div>
        </>
      )
    }

    if (isAggregatorChannel && addedOptionNumber === 0) {
      return null;
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Extra cover'}</div>
          {isMobile && (<div className={'flex--stretch text-right'}>
              <EditSectionButton
                isCalculating={isCalculating}
                handleEdit={editOptions}
                hidden={isAggregatorChannel}/>
            </div>
          )}
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'ShieldPlus'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div className={'title'}>{addedOptionNumber + ' upgrades added'}</div>
              </div>
            </div>
          </div>
          {!isMobile && (<div className={'flex--stretch text-right'}>
              <EditSectionButton
                isCalculating={isCalculating}
                handleEdit={editOptions}
                hidden={isAggregatorChannel}/>
            </div>
          )}
        </div>

        {map(additionalCover, (option, optionKey) => {

          const isGolfCover = optionKey === 'golf_cover';
          const isGadgetCover = optionKey === 'gadget_cover';
          const isCruiseCover = optionKey === 'cruise_cover';
          const isWinterSportsCover = optionKey === 'winter_sports';
          const text = get(product, `metadata.definitions.${optionKey}_alt_title_new`, get(product, `metadata.definitions.${optionKey}_alt_title`))
          let bodyText = ((isGolfCover || isCruiseCover || isWinterSportsCover) && get(product, 'metadata')) ? text : product.metadata.definitions[`${optionKey}_alt_${coverLevel}`];

          if (isWinterSportsCover) {
            const wsBenefit = find(get(premium, 'information.allBenefits', []), ['name', 'Winter sports'])
            const skyEquipment = get(get(wsBenefit, 'sub_benefits', []).find((sb) => sb.name.includes('Ski equipment')), 'cover_level');
            if (get(skyEquipment, coverLevel, null)) {
              bodyText = bodyText.replace('{alt_value}', get(skyEquipment, coverLevel))
            }
          }

          if (isGadgetCover) {
            const extraGadgetText = get(product, `metadata.definitions.${optionKey}_alt_dynamic_new`)
            const dynamicText = replaceBenefitTextPlaceHolder(extraGadgetText, get(premium, 'information.allBenefits', []), 'gadget_cover', true, coverLevel)
            bodyText = bodyText + '\n\n' + dynamicText;
          }

          if (get(options, optionKey)) {
            const totalPrice = get(premium, `information.boltOns.${optionKey}.total`)
            const currency = get(premium, `currency`)

            const formatted = (
              <FormattedNumber
                value={totalPrice}
                style="currency"
                currency={currency}
              />
            )

            return (
              <div className={'flex-container'} key={optionKey}>
                <div className={'flex--20'}/>
                <div className={'flex--stretch'}>
                  <div className={'traveller-info-container'}>
                    <div>{renderOptionIcons(optionKey)}</div>
                    <div className={'traveller-info single'}>
                      <div>{option.name}</div>
                      <div className={'sub-title'}>Added for {formatted} total</div>
                    </div>
                  </div>
                </div>
                <div className={'info-popup'}>INFO <DefinitionPopup
                  name="benefit-info"
                  definition={bodyText}
                  iconType="info"
                /></div>
              </div>
            )
          }
          return null
        })}

      </div>
    )
  }

  const renderOptionIcons = (optionName) => {
    return (<QuoteIcons name={optionName} color={'blue'}/>);
  }

  const renderDates = () => {
    const tripDuration = isAnnual ? 365 : getTripDuration(startDate, endDate, 1);

    if (showEditDates) {
      return (
        <AdmiralCard icon={(<QuoteIcons name={'Date'} color={'white'}/>)}
                     title={isAnnual ? 'Select your start date' : 'Select your trip dates'}>
          <div>
            <StartDateSelector
              tripType={tripType}
              renderDefinition={renderDefinition}
              change={props.change}
              startDate={startDate}/>
            {!isAnnual && (
              <>
                <EndDateSelector startDate={startDate} renderDefinition={renderDefinition}/>
              </>
            )}
            <RecalculateButton handleRecalculate={handleRecalculate()} valid={valid} block={true}
                               isCalculating={quoteState.isCalculating}/>
          </div>
        </AdmiralCard>
      )
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Cover start'}</div>
          {isMobile && (<div className={'flex--stretch text-right'}>
              <EditSectionButton
                isCalculating={isCalculating}
                handleEdit={editTripDates}
                hidden={isAggregatorChannel}/>
            </div>
          )}
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Calendar'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div className={'title'}>{`${tripDuration} days cover`}</div>
                <div>{`Starting ${startDate}`}</div>
                {!isAnnual && (
                  <div>{`Ending ${endDate}`}</div>
                )}
              </div>
            </div>
          </div>
          {!isMobile && (<div className={'flex--stretch text-right'}>
              <EditSectionButton
                isCalculating={isCalculating}
                handleEdit={editTripDates}
                hidden={isAggregatorChannel}/>
            </div>
          )}
        </div>
        <div className={'flex-container'}>
          <div className={'flex--20'}/>
          <div className={'flex--stretch'}>
            <div className={'cover-level-list no-padding'}>
              <QuoteIcons name={'Tick'} color={'green'}/>
              <div
                className={'sub-title'}>{'You are covered for holiday cancellation as soon as your cover starts.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDocuments = () => {
    const documentLink = get(premium, `scheme.documents.Insurance Product Information Document`)
    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Documents'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'IPID'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div><a href={documentLink} target={'_blank'}>Insurance Product Information Document</a></div>
                <div>{`Contains a summary of your insurance cover`}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderMedicalQuestionModal = () => {
    return (
      <BootstrapModal
        show={showMedicalScreeningModal}
        onHide={closeMedicalModal}
        keyboard={false}
        animation={true}
        backdrop="static"
      >
        <ModalHeader closeButton={true}>
          <ModalTitle>Medical Conditions</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div>Can you confirm that there are no medical conditions to declare?</div>
        </ModalBody>
        <ModalFooter>
          <Row>
            <Col xs={6}>
              <Button
                type="button"
                block={true}
                onClick={handleNavigateToMedical}
                className={'admiral-primary-btn'}>
                NO
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                type="button"
                block={true}
                onClick={closeMedicalModal}
                className={'admiral-primary-btn'}>
                YES
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </BootstrapModal>
    )
  }

  return (
    <>
      {!isAggregatorChannel && renderTravellersCard()}
      {isAggregatorChannel && renderAggregatorTravellersCard()}
      {renderDestinationRegion()}
      {renderLevelOfCover()}
      {renderExtraCover()}
      {renderDates()}
      {renderDocuments()}
      {renderMedicalQuestionModal()}
    </>
  )
}
